import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDOP } from "@/lib/format";
import { suggestRetentions, TIPOS_GASTO_606 } from "@/lib/retentions";
import { ExpenseLines, EMPTY_LINE } from "@/components/expenses/ExpenseLines";
import { ExpenseAdvancesBox } from "@/components/expenses/ExpenseAdvancesBox";

export const EXPENSE_DOC_TYPES = [
  { value: "comprobante", label: "Con comprobante del suplidor (e-CF: E31…)", hint: "El suplidor formal te dio su factura con NCF. Amelia no emite nada; registra su NCF para el 606." },
  { value: "E41", label: "E41 · Compra o servicio a persona informal", hint: "Persona física no inscrita que no puede darte comprobante (plomero, chofer, reparación, cena de turno extra…). Amelia emite el E41 y aplica las retenciones." },
  { value: "E43", label: "E43 · Gastos menores", hint: "Peajes, parqueos, transporte, alimentación en diligencias. Sin suplidor: indica quién incurrió el gasto y el concepto de cada línea." },
  { value: "E47", label: "E47 · Pago al exterior", hint: "Servicios de proveedores extranjeros no residentes. Amelia emite el E47 con la retención de ISR." },
];

const SUPPLIER_FILTER = { E41: (s) => s.supplier_type === "informal" || (s.rnc_cedula || "").replace(/-/g, "").length === 11,
  E47: (s) => s.supplier_type === "foreign", comprobante: (s) => s.supplier_type !== "foreign" };

const localToday = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };

const blank = () => ({ doc_type: "comprobante", date: localToday(), supplier_id: "", supplier_ncf: "",
  incurred_by: "", payment_method: "efectivo", tipo_gasto_606: "02", notes: "" });

function totals(lines, docType, rate) {
  const exento = docType === "E43" || docType === "E47";
  let subtotal = 0, itbis = 0, services = 0;
  lines.forEach((l) => {
    const amt = Number(l.quantity || 0) * Number(l.price || 0);
    subtotal += amt;
    if (l.itbis_applies && !exento) itbis += amt * rate / 100;
    if (l.is_service || exento) services += amt;
  });
  return { subtotal, itbis, total: subtotal + itbis, servicesSubtotal: services, exento };
}

export function ExpenseFormDialog({ open, onClose, onSaved }) {
  const [f, setF] = useState(blank());
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [ret, setRet] = useState({ itbis_retenido: 0, isr_retenido: 0 });
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [grossUp, setGrossUp] = useState(false);
  const [advanceIds, setAdvanceIds] = useState([]);

  useEffect(() => {
    if (!open) return;
    setF(blank()); setLines([{ ...EMPTY_LINE }]); setGrossUp(false); setAdvanceIds([]);
    api.get("/suppliers").then(({ data }) => setSuppliers(data));
    api.get("/expense-categories").then(({ data }) => setCategories(data));
  }, [open]);

  const t = useMemo(() => totals(lines, f.doc_type, 18), [lines, f.doc_type]);
  useEffect(() => { setRet(suggestRetentions(f.doc_type, { ...t, date: f.date })); }, [f.doc_type, t, f.date]);
  const docInfo = EXPENSE_DOC_TYPES.find((d) => d.value === f.doc_type);
  const filter = SUPPLIER_FILTER[f.doc_type];
  const supplierOptions = filter ? suppliers.filter(filter) : [];

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/expenses", {
        ...f, supplier_id: f.supplier_id || null,
        items: lines.map((l) => ({ ...l, quantity: Number(l.quantity), price: Number(l.price) })),
        itbis_retenido: Number(ret.itbis_retenido || 0), isr_retenido: Number(ret.isr_retenido || 0),
        gross_up: grossUp, advance_ids: advanceIds,
      });
      const extra = data.gross_up ? ` · declarado ${formatDOP(data.total)} (retenciones asumidas)` : "";
      const adv = data.advance_applied ? ` · anticipo aplicado ${formatDOP(data.advance_applied)}` : "";
      toast.success(`Gasto ${data.number} registrado${extra}${adv}${data.is_electronic ? ` · enviando ${data.doc_type} a DGII` : ""}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const sel = "mt-1 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="expense-form-dialog">
        <DialogHeader><DialogTitle>Nuevo gasto</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label className="overline">Tipo de documento</Label>
            <select className={sel} value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value, supplier_id: "" })} data-testid="expense-doc-type">
              {EXPENSE_DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="expense-doc-hint">{docInfo?.hint}</p>
          </div>
          <div><Label className="overline">Fecha</Label><Input type="date" className="mt-1" value={f.date} onChange={set("date")} data-testid="expense-date" /></div>

          {f.doc_type === "E43" ? (
            <div className="md:col-span-2"><Label className="overline">Incurrido por</Label>
              <Input className="mt-1" placeholder="Empleado o responsable (ej. Juan Pérez · chofer)" value={f.incurred_by} onChange={set("incurred_by")} data-testid="expense-incurred-by" /></div>
          ) : (
            <div className="md:col-span-2"><Label className="overline">Suplidor</Label>
              <select className={sel} value={f.supplier_id} onChange={set("supplier_id")} data-testid="expense-supplier">
                <option value="">Selecciona…</option>
                {supplierOptions.map((s) => <option key={s.id} value={s.id}>{s.name}{s.rnc_cedula ? ` · ${s.rnc_cedula}` : ""}</option>)}
              </select>
              {supplierOptions.length === 0 && <p className="text-xs text-amber-700 mt-1">No hay suplidores de este tipo. Créalo en Suplidores con el tipo correcto.</p>}
            </div>
          )}
          {f.doc_type === "comprobante" && (
            <div><Label className="overline">NCF del suplidor</Label>
              <Input className="mt-1 font-mono uppercase" placeholder="E310000000001" value={f.supplier_ncf} onChange={set("supplier_ncf")} data-testid="expense-supplier-ncf" /></div>
          )}
          <div><Label className="overline">Pago</Label>
            <select className={sel} value={f.payment_method} onChange={set("payment_method")} data-testid="expense-payment">
              <option value="efectivo">Efectivo</option><option value="transferencia">Transferencia</option>
              <option value="cheque">Cheque</option><option value="tarjeta">Tarjeta</option>
            </select></div>
          <div><Label className="overline">Tipo de gasto (606)</Label>
            <select className={sel} value={f.tipo_gasto_606} onChange={set("tipo_gasto_606")} data-testid="expense-606">
              {TIPOS_GASTO_606.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
            </select></div>
        </div>

        <div className="mt-2"><Label className="overline">Líneas</Label>
          <div className="mt-1"><ExpenseLines lines={lines} setLines={setLines} categories={categories} exento={t.exento} docType={f.doc_type} /></div>
        </div>

        <ExpenseSummary t={t} ret={ret} setRet={setRet} docType={f.doc_type} grossUp={grossUp} />

        <ExpenseAdvancesBox
          docType={f.doc_type} supplierId={f.supplier_id} incurredBy={f.incurred_by}
          total={t.total} net={t.total - Number(ret.itbis_retenido || 0) - Number(ret.isr_retenido || 0)}
          grossUp={grossUp} setGrossUp={setGrossUp} selected={advanceIds} setSelected={setAdvanceIds}
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-primary font-bold" onClick={submit} disabled={busy} data-testid="expense-submit">
            {f.doc_type === "comprobante" ? "Registrar gasto" : `Registrar y emitir ${f.doc_type}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseSummary({ t, ret, setRet, docType, grossUp }) {
  const showItbisRet = (docType === "E41" || docType === "comprobante") && !grossUp;
  const showIsrRet = docType !== "E43" && !grossUp;
  const net = grossUp ? t.total : t.total - Number(ret.itbis_retenido || 0) - Number(ret.isr_retenido || 0);
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-muted/40 rounded-md p-3 text-sm" data-testid="expense-summary">
      <div><div className="overline">Subtotal</div><div className="font-mono font-bold">{formatDOP(t.subtotal)}</div></div>
      <div><div className="overline">ITBIS</div><div className="font-mono font-bold" data-testid="expense-itbis">{formatDOP(t.itbis)}</div></div>
      {showItbisRet ? (
        <div><div className="overline">ITBIS retenido</div>
          <Input type="number" step="0.01" className="h-8 font-mono" value={ret.itbis_retenido} onChange={(e) => setRet({ ...ret, itbis_retenido: e.target.value })} data-testid="expense-itbis-ret" /></div>
      ) : <div />}
      {showIsrRet ? (
        <div><div className="overline">ISR retenido</div>
          <Input type="number" step="0.01" className="h-8 font-mono" value={ret.isr_retenido} onChange={(e) => setRet({ ...ret, isr_retenido: e.target.value })} data-testid="expense-isr-ret" /></div>
      ) : <div />}
      <div><div className="overline">Neto a pagar</div><div className="font-mono font-bold text-primary" data-testid="expense-net">{formatDOP(net)}</div></div>
      {(docType === "E41" || docType === "E47") && (
        <p className="col-span-full text-xs text-muted-foreground">Retenciones sugeridas por Amelia según la norma DGII; puedes ajustarlas.</p>
      )}
    </div>
  );
}
